/**
 * TournamentBracket.jsx
 * Knockout bracket visualization components.
 * BracketMatchCard, BracketConnector, BracketView
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Trophy, GitBranch, Target } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

// ── Constants ─────────────────────────────────────────────────────────────────
const CARD_W   = 220;  // px match card width
const CARD_H   = 80;   // px match card height
const CONN_W   = 52;   // px SVG connector width
const INIT_GAP = 16;   // px gap between first-round cards

const ROUND_LABELS = {
  rr_r1: 'Round 1', rr_r2: 'Round 2', rr_r3: 'Round 3', rr_r4: 'Round 4',
  rr_r5: 'Round 5', rr_r6: 'Round 6', rr_r7: 'Round 7',
  qf: 'Quarter Finals', sf: 'Semi Finals', f: 'Final', '3rd': '3rd Place',
  sf_m1: 'Semi Final 1', sf_m2: 'Semi Final 2', sf_m3: 'Semi Final 3', sf_m4: 'Semi Final 4',
  final_m3: 'Final', final_m5: 'Final',
};

const ROUND_GROUP = {
  r32_m1:'r32', r32_m2:'r32', r32_m3:'r32', r32_m4:'r32',
  r32_m5:'r32', r32_m6:'r32', r32_m7:'r32', r32_m8:'r32',
  r16_m1:'r16', r16_m2:'r16', r16_m3:'r16', r16_m4:'r16',
  qf_m1:'qf',  qf_m2:'qf',  qf_m3:'qf',  qf_m4:'qf',
  sf_m1:'sf',  sf_m2:'sf',  sf_m3:'sf',  sf_m4:'sf',
  final_m3:'f', final_m5:'f',
};

const KO_COL_ORDER  = ['r32', 'r16', 'qf', 'sf', 'f', '3rd'];
const KO_COL_LABELS = {
  r32: 'Round of 32', r16: 'Round of 16',
  qf:  'Quarter Finals', sf: 'Semi Finals',
  f:   'Final',          '3rd': '3rd Place',
};

const formatRound    = (r)   => ROUND_LABELS[r] || r?.replace(/_/g,' ') || 'Round';
const formatColLabel = (col) => KO_COL_LABELS[col] || ROUND_LABELS[col] || formatRound(col);

// Compute y-positions for every match in every column
function computeLayout(cols, colMap) {
  const layout = {};
  cols.forEach((col, ri) => {
    const matches = colMap[col];
    if (ri === 0) {
      layout[col] = matches.map((fx, i) => ({ fx, y: i * (CARD_H + INIT_GAP) }));
    } else {
      const prev = layout[cols[ri - 1]];
      layout[col] = matches.map((fx, i) => {
        const p1 = prev[i * 2]; const p2 = prev[i * 2 + 1];
        if (p1 && p2) return { fx, y: (p1.y + p2.y + CARD_H) / 2 - CARD_H / 2 };
        if (p1)       return { fx, y: p1.y };
        return { fx, y: i * (CARD_H + INIT_GAP) };
      });
    }
  });
  return layout;
}

// ── BracketMatchCard ──────────────────────────────────────────────────────────
function BracketMatchCard({ fx, schedule, isHovered, isConnected, isActive, onHover }) {
  const home = fx.home || fx.team1 || {};
  const away = fx.away || fx.team2 || {};
  const isDone = ['completed','finished'].includes(fx.status?.toLowerCase());
  const isLive = fx.status?.toLowerCase() === 'live';
  const homeWon = isDone && fx.winner_side === 'home';
  const awayWon = isDone && fx.winner_side === 'away';
  const sched   = schedule?.[fx.id];
  const dimmed  = isActive && !isHovered && !isConnected;
  const fid     = fx.match_id || fx.id;

  return (
    <div
      style={{ width: CARD_W, height: CARD_H }}
      className={`bg-[#161a26] border rounded-lg overflow-hidden text-sm flex-shrink-0 flex flex-col cursor-pointer select-none transition-all duration-200
        ${isHovered    ? 'border-[#f5c518] shadow-[0_0_16px_rgba(245,197,24,0.28)] scale-[1.02]' : ''}
        ${isConnected && !isHovered ? 'border-[rgba(245,197,24,0.45)]' : ''}
        ${isLive && !isHovered && !isConnected ? 'border-[rgba(239,68,68,0.4)] ring-1 ring-[rgba(239,68,68,0.15)]' : ''}
        ${!isHovered && !isConnected && !isLive ? 'border-[#252a37]' : ''}
        ${dimmed ? 'opacity-30' : 'opacity-100'}`}
      onMouseEnter={() => onHover?.(fx.id)}
      onMouseLeave={() => onHover?.(null)}
    >
      <div className={`flex items-center justify-between px-3 py-1.5 border-b border-[#252a37] flex-1 ${homeWon ? 'bg-[rgba(245,197,24,0.05)]' : ''}`}>
        <span className={`truncate max-w-[130px] text-xs font-semibold ${homeWon ? 'text-[#f5c518]' : 'text-[#f0f2f7]'}`}>
          {home.name || home.short_name || 'TBD'}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className={`font-bold tabular-nums text-xs ${isLive ? 'text-[#f5c518]' : 'text-[#8a93a3]'}`}>
            {isDone || isLive ? (fx.home_score ?? fx.score?.home ?? '–') : ''}
          </span>
          {homeWon && <Trophy className="h-2.5 w-2.5 text-[#f5c518]" />}
        </div>
      </div>
      <div className={`flex items-center justify-between px-3 py-1.5 flex-1 ${awayWon ? 'bg-[rgba(245,197,24,0.05)]' : ''}`}>
        <span className={`truncate max-w-[130px] text-xs font-semibold ${awayWon ? 'text-[#f5c518]' : 'text-[#f0f2f7]'}`}>
          {away.name || away.short_name || 'TBD'}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className={`font-bold tabular-nums text-xs ${isLive ? 'text-[#f5c518]' : 'text-[#8a93a3]'}`}>
            {isDone || isLive ? (fx.away_score ?? fx.score?.away ?? '–') : ''}
          </span>
          {awayWon && <Trophy className="h-2.5 w-2.5 text-[#f5c518]" />}
        </div>
      </div>
      {sched?.time && (
        <div className="border-t border-[#252a37] px-3 py-1 text-center">
          <span className="text-[#8a93a3] text-[10px] flex items-center justify-center gap-1">
            <Clock className="h-2.5 w-2.5" />{sched.time}{sched.court && ` · ${sched.court}`}
          </span>
        </div>
      )}
      {!sched?.time && (fx.status === 'live' || fx.status === 'scheduled') && fid && (
        <div className="border-t border-[#252a37] px-3 py-0.5 text-center">
          <Link to={`/score/${fid}`} onClick={e => e.stopPropagation()}>
            <Button size="sm" variant="ghost" className="h-5 text-[10px] text-[#f5c518] hover:text-[#f7d24a] px-1.5 py-0">
              <Target className="h-2.5 w-2.5 mr-1" />
              {isLive ? 'Live' : 'Score'}
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}

// ── BracketConnector ──────────────────────────────────────────────────────────
function BracketConnector({ prevLayout, nextLayout, totalH, hoveredId }) {
  const hovPrevIdx = prevLayout.findIndex(({ fx }) => fx.id === hoveredId);
  const hovNextIdx = nextLayout.findIndex(({ fx }) => fx.id === hoveredId);
  const anyHov     = hovPrevIdx !== -1 || hovNextIdx !== -1;
  const segments   = [];

  nextLayout.forEach(({ y: childY }, i) => {
    const p1 = prevLayout[i * 2]; const p2 = prevLayout[i * 2 + 1];
    if (!p1) return;
    const childCY = childY + CARD_H / 2; const midX = CONN_W / 2;
    const childHov = hovNextIdx === i;
    const p1Hov   = hovPrevIdx === i * 2;
    const p2Hov   = p2 && hovPrevIdx === i * 2 + 1;
    const highlight = p1Hov || p2Hov || childHov;
    const dim = anyHov && !highlight;
    const gold = '#f5c518'; const grey = '#3a4155';

    if (p2) {
      const p1CY = p1.y + CARD_H / 2; const p2CY = p2.y + CARD_H / 2;
      segments.push({ d: `M 0,${p1CY} H ${midX}`,     color: (p1Hov||childHov)?gold:grey, w:(p1Hov||childHov)?2:1.5, o:(!anyHov||p1Hov||childHov)?1:0.18 });
      segments.push({ d: `M 0,${p2CY} H ${midX}`,     color: (p2Hov||childHov)?gold:grey, w:(p2Hov||childHov)?2:1.5, o:(!anyHov||p2Hov||childHov)?1:0.18 });
      segments.push({ d: `M ${midX},${p1CY} V ${p2CY}`,color: childHov?gold:grey,         w:childHov?2:1.5,           o:(!anyHov||childHov)?1:0.18 });
      segments.push({ d: `M ${midX},${childCY} H ${CONN_W}`, color:(p1Hov||p2Hov||childHov)?gold:grey, w:highlight?2:1.5, o:(!anyHov||highlight)?1:0.18 });
    } else {
      const p1CY = p1.y + CARD_H / 2;
      const hi = p1Hov || childHov;
      segments.push({ d: `M 0,${p1CY} H ${CONN_W}`, color:hi?gold:grey, w:hi?2:1.5, o:(!anyHov||hi)?1:0.18 });
    }
  });

  return (
    <svg width={CONN_W} height={totalH} style={{ flexShrink: 0, overflow: 'visible' }}>
      {segments.map(({ d, color, w, o }, i) => (
        <path key={i} d={d} fill="none" stroke={color} strokeWidth={w} strokeLinecap="round"
          style={{ opacity: o, transition: 'stroke 0.15s, opacity 0.15s, stroke-width 0.15s' }}
        />
      ))}
    </svg>
  );
}

// ── BracketView ───────────────────────────────────────────────────────────────
export function BracketView({ fixtures, schedule }) {
  const [hoveredId, setHoveredId] = useState(null);

  const colMap = fixtures.reduce((acc, fx) => {
    const col = ROUND_GROUP[fx.round || ''] || fx.round || 'unknown';
    if (!acc[col]) acc[col] = [];
    acc[col].push(fx);
    return acc;
  }, {});
  Object.values(colMap).forEach(arr => arr.sort((a, b) => (a.round||'').localeCompare(b.round||'')));

  const orderedCols = [
    ...KO_COL_ORDER.filter(c => colMap[c]),
    ...Object.keys(colMap).filter(c => !KO_COL_ORDER.includes(c)),
  ];

  if (orderedCols.length === 0) {
    return (
      <Card className="bg-[#161a26] border-[#252a37]">
        <CardContent className="p-12 text-center">
          <GitBranch className="h-12 w-12 text-[#8a93a3] mx-auto mb-4" />
          <p className="text-[#8a93a3]">No bracket data yet. Generate fixtures first.</p>
        </CardContent>
      </Card>
    );
  }

  const layout = computeLayout(orderedCols, colMap);

  const connectedIds = new Set();
  if (hoveredId) {
    orderedCols.forEach((col, ci) => {
      const items = layout[col];
      const idx   = items?.findIndex(({ fx }) => fx.id === hoveredId) ?? -1;
      if (idx === -1) return;
      const nextCol = orderedCols[ci + 1];
      if (nextCol) { const ci2 = Math.floor(idx/2); layout[nextCol]?.[ci2] && connectedIds.add(layout[nextCol][ci2].fx.id); }
      const prevCol = orderedCols[ci - 1];
      if (prevCol) {
        const p1 = layout[prevCol]?.[idx*2]; const p2 = layout[prevCol]?.[idx*2+1];
        if (p1) connectedIds.add(p1.fx.id);
        if (p2) connectedIds.add(p2.fx.id);
      }
    });
  }

  const colH = (col) => {
    const items = layout[col];
    return items?.length ? items[items.length-1].y + CARD_H : CARD_H;
  };
  const anyHovered = !!hoveredId;

  return (
    <div className="overflow-x-auto pb-6">
      <div className="inline-flex items-start gap-0 p-4 min-w-max">
        {orderedCols.map((col, ci) => {
          const items = layout[col]; const h = colH(col);
          return (
            <div key={col} className="flex items-start gap-0">
              {ci > 0 && (
                <BracketConnector
                  prevLayout={layout[orderedCols[ci-1]]}
                  nextLayout={items}
                  totalH={Math.max(colH(orderedCols[ci-1]), h) + 8}
                  hoveredId={hoveredId}
                />
              )}
              <div className="flex flex-col items-center">
                <span className="text-[10px] font-semibold text-[#8a93a3] uppercase tracking-wider mb-3 whitespace-nowrap">
                  {formatColLabel(col)}
                </span>
                <div style={{ position: 'relative', width: CARD_W, height: h }}>
                  {items.map(({ fx, y }) => (
                    <div key={fx.id} style={{ position: 'absolute', top: y, left: 0 }}>
                      <BracketMatchCard
                        fx={fx} schedule={schedule}
                        isHovered={hoveredId === fx.id}
                        isConnected={connectedIds.has(fx.id)}
                        isActive={anyHovered}
                        onHover={setHoveredId}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
